import { supabase } from './supabase.js';
import { getStudentType } from '../utils/collegeTimelineCalculator.js';
import EDValidationService from '../services/EDValidationService.js';

export const collegeJourneyProgressService = {
  // Get college journey progress for a user
  async getProgress(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_progress')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error getting college journey progress:', error);
      return null;
    }
  },

  // Save/update college journey progress
  async saveProgress(userId, progress) {
    try {
      // Check existing status to prevent regression from true to false
      const { data: existingProgress } = await supabase
        .from('college_journey_progress')
        .select('onboarding_complete')
        .eq('user_id', userId)
        .single();

      // If onboarding is already complete and we're trying to set it to false, preserve the true status
      const onboardingComplete = existingProgress?.onboarding_complete === true && progress.onboardingComplete === false
        ? true
        : progress.onboardingComplete || false;

      const { data, error } = await supabase
        .from('college_journey_progress')
        .upsert({
          user_id: userId,
          onboarding_complete: onboardingComplete,
          current_step: progress.currentStep || 1,
          total_steps: progress.totalSteps || 4,
          profile_data: progress.profileData || null,
          selected_colleges: progress.selectedColleges || null,
          college_preferences: progress.collegePreferences || null,
          last_updated: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error saving college journey progress:', error);
      throw error;
    }
  },

  // Mark onboarding as complete
  async markOnboardingComplete(userId) {
    try {
      // This function should only ever set onboarding_complete to true
      // Add logging to track when this is called
      console.log('🎯 Marking onboarding complete for user:', userId);

      const { data, error } = await supabase
        .from('college_journey_progress')
        .upsert({
          user_id: userId,
          onboarding_complete: true,
          last_updated: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      console.log('✅ Successfully marked onboarding complete');
      return data;
    } catch (error) {
      console.error('❌ Error marking onboarding complete:', error);
      throw error;
    }
  },

  // Check if user has access to college hub
  async hasCollegeHubAccess(userId) {
    try {
      const progress = await this.getProgress(userId);
      return progress?.onboarding_complete || false;
    } catch (error) {
      console.error('Error checking college hub access:', error);
      return false;
    }
  },

  // Reset onboarding progress for testing (clears only progress, preserves colleges/profiles)
  async resetOnboardingProgress(userId) {
    try {
      console.log('🔄 Resetting onboarding progress for user:', userId);
      
      // Delete from database (filter by user_id only)
      const { error: dbError } = await supabase
        .from('college_journey_progress')
        .delete()
        .eq('user_id', userId);

      if (dbError) {
        console.error('Database reset error:', dbError);
        throw dbError;
      }

      // Clear relevant localStorage keys
      const keysToRemove = [
        'college-onboarding-complete',
        'student-onboarding-progress',
        `student-onboarding-progress-${userId}`,
        'student-onboarding-data'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log('Cleared localStorage key:', key);
      });

      console.log('✅ Successfully reset onboarding progress for user:', userId);
      return { success: true, message: 'Onboarding progress reset successfully' };
      
    } catch (error) {
      console.error('Error resetting onboarding progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Store generated prompts for caching
  async saveGeneratedPrompts(userId, prompts) {
    try {
      console.log('💾 Saving generated prompts for user:', userId);
      
      // First, get or create the current freewrite record
      const today = new Date().toISOString().split('T')[0];
      const freewrite = await collegeJourneyService.getOrCreateFreeWrite(today, userId, null);
      
      if (!freewrite) {
        throw new Error('Could not get or create freewrite record');
      }
      
      // Update the record with generated prompts and timestamp
      const { data, error } = await supabase
        .from('college_journey_freewrites')
        .update({
          generated_prompts: prompts,
          prompts_generated_at: new Date().toISOString()
        })
        .eq('id', freewrite.id)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Successfully saved generated prompts');
      return data;
      
    } catch (error) {
      console.error('Error saving generated prompts:', error);
      throw error;
    }
  },

  // Get cached prompts if they exist and are fresh (less than 7 days old)
  async getCachedPrompts(userId) {
    try {
      console.log('🔍 Checking for cached prompts for user:', userId);
      
      // Look for the most recent prompts for this user
      const { data, error } = await supabase
        .from('college_journey_freewrites')
        .select('generated_prompts, prompts_generated_at')
        .eq('user_id', userId)
        .not('generated_prompts', 'is', null)
        .order('prompts_generated_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // No cached prompts found
          console.log('No cached prompts found');
          return null;
        }
        throw error;
      }
      
      if (!data || !data.generated_prompts) {
        return null;
      }
      
      // Check if cache is still fresh (7 days)
      const cacheAge = Date.now() - new Date(data.prompts_generated_at).getTime();
      const maxCacheAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
      
      if (cacheAge > maxCacheAge) {
        console.log('⏰ Cached prompts expired (older than 7 days)');
        return null;
      }
      
      console.log('✅ Found fresh cached prompts');
      return data.generated_prompts;
      
    } catch (error) {
      console.error('Error getting cached prompts:', error);
      return null;
    }
  }
};

// Main college journey service for managing college data
export const collegeJourneyService = {
  // Re-export getStudentType for convenience
  getStudentType,
  
  async searchGlobalColleges(searchTerm, limit = 10) {
    try {
      if (!searchTerm || searchTerm.length < 2) return [];
      
      const { data, error } = await supabase
        .from('global_colleges')
        .select('id, name, location, ranking, acceptance_rate')
        .or(`name.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`)
        .order('ranking', { ascending: true, nullsFirst: false })
        .limit(limit);
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching global colleges:', error);
      return [];
    }
  },

  async fetchCollegeDataWithAI(collegeId, collegeName, dataFields = 'basic') {
    try {
      console.log('🤖 Fetching AI data for college:', collegeName);
      
      // Call the AI API
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'fetch-college-data',
          collegeName,
          dataFields 
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch college data');
      }
      
      const result = await response.json();
      console.log('✅ AI data fetched successfully:', result.data);
      
      // Update the college with the fetched data
      const updatedCollege = await this.updateCollege(collegeId, result.data);
      
      return {
        success: true,
        college: updatedCollege,
        dataYear: result.dataYear,
        completeness: result.data.data_completeness
      };
      
    } catch (error) {
      console.error('Error fetching college data with AI:', error);
      throw error;
    }
  },

  async getCollegeDataCompleteness(collegeId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('data_completeness, data_last_updated, name')
        .eq('id', collegeId)
        .single();
        
      if (error) throw error;
      
      return {
        completeness: data.data_completeness || 0,
        lastUpdated: data.data_last_updated,
        name: data.name,
        needsUpdate: !data.data_last_updated || 
                     new Date() - new Date(data.data_last_updated) > 30 * 24 * 60 * 60 * 1000 // 30 days
      };
    } catch (error) {
      console.error('Error checking college data completeness:', error);
      return { completeness: 0, needsUpdate: true };
    }
  },
  // College management
  async getColleges(userId) {
    try {
      console.log('🔍 getColleges: Querying for userId:', userId);
      
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      console.log('🔍 getColleges: Found', data?.length || 0, 'colleges for user:', userId);
      
      // Debug: Check if ai_suggestions are being loaded
      if (data && data.length > 0) {
        data.forEach((college, index) => {
          console.log(`🔍 College ${index + 1} (${college.name}):`, {
            hasAiSuggestions: !!college.ai_suggestions,
            aiSuggestionsKeys: college.ai_suggestions ? Object.keys(college.ai_suggestions) : [],
            aiSuggestionsUpdatedAt: college.ai_suggestions_updated_at
          });
        });
      }
      
      return data || [];
    } catch (error) {
      console.error('Error getting colleges:', error);
      return [];
    }
  },
  
  async addCollege(collegeData, userId) {
    try {
      console.log('🎓 addCollege: Adding college for userId:', userId, 'College:', collegeData.name);

      // ED VALIDATION: Check if trying to add as Early Decision
      if (collegeData.application_round === 'early_decision') {
        const validation = await EDValidationService.validateApplicationRound(
          userId,
          null, // null because this is a new college
          'early_decision'
        );

        if (!validation.isValid) {
          console.error('🚫 ED Validation failed:', validation.message);
          throw new Error(validation.message);
        }
      }

      const collegeName = collegeData.name || collegeData.college_name;
      let globalCollegeId = collegeData.global_college_id || null;
      
      // Step 1: Check if this college already exists in global_colleges
      if (!globalCollegeId && collegeName) {
        console.log('🔍 Searching for college in global_colleges:', collegeName);
        
        // Normalize college name for better matching
        const normalizeCollegeName = (name) => {
          return name
            .toLowerCase()
            .replace(/\s*(university|college|school|institute|of|the)\s*/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        };
        
        const normalizedInput = normalizeCollegeName(collegeName);
        console.log('🔍 Normalized search term:', normalizedInput);
        
        // Search for exact match first
        const { data: exactMatch } = await supabase
          .from('global_colleges')
          .select('id, name, location')
          .ilike('name', collegeName)
          .single();
          
        if (exactMatch) {
          console.log('✅ Found exact match in global_colleges:', exactMatch);
          globalCollegeId = exactMatch.id;
        } else {
          // Search for intelligent matches
          const { data: allColleges } = await supabase
            .from('global_colleges')
            .select('id, name, location')
            .or(`name.ilike.%${collegeName.split(' ')[0]}%`); // Search by first word (e.g., "Georgetown")
            
          if (allColleges && allColleges.length > 0) {
            // Find best match using normalized names
            const matches = allColleges.filter(college => {
              const normalizedExisting = normalizeCollegeName(college.name);
              // Check if core institution name matches
              return normalizedInput.includes(normalizedExisting.split(' ')[0]) || 
                     normalizedExisting.includes(normalizedInput.split(' ')[0]);
            });
            
            if (matches.length === 1) {
              console.log('✅ Found intelligent match in global_colleges:', matches[0]);
              globalCollegeId = matches[0].id;
            } else if (matches.length > 1) {
              // For now, log multiple matches but don't auto-select
              console.log('⚠️ Found multiple potential matches:', matches);
              // In the future, we could present these options to the user
            }
          }
        }
      }
      
      // Step 2: Check if user already has this college
      if (globalCollegeId) {
        const { data: existingEntry } = await supabase
          .from('college_journey_colleges')
          .select('id, name, category')
          .eq('user_id', userId)
          .eq('global_college_id', globalCollegeId)
          .single();
          
        if (existingEntry) {
          console.log('⚠️ User already has this college in their list:', existingEntry);
          return { ...existingEntry, isDuplicate: true };
        }
      }
      
      // Step 3: Require global college match - reject if not found
      if (!globalCollegeId) {
        console.log('❌ No global college match found for:', collegeName);
        throw new Error(`College "${collegeName}" not found in our database. Please select from available colleges or request to add this college.`);
      }
      
      // Step 4: Insert into college_journey_colleges with required global link
      const insertData = {
        user_id: userId,
        suggested_by: userId,
        name: collegeName,
        location: collegeData.location,
        category: collegeData.category || 'target',
        suggestion_source: collegeData.suggestion_source || 'student',
        global_college_id: globalCollegeId,
        source: collegeData.source,
        notes: collegeData.notes
      };
      
      console.log('🎓 addCollege: Insert data:', insertData);
      
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .insert(insertData)
        .select()
        .single();
      
      if (error) {
        console.error('🎓 addCollege: Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log('🎓 addCollege: Successfully added college:', data);
      
      // Auto-generate immediate milestone templates for this college
      try {
        console.log('🎓 addCollege: Auto-generating immediate milestone templates...');
        const AdminMilestoneService = (await import('../services/AdminMilestoneService')).default;
        const milestoneResult = await AdminMilestoneService.generateCollegeMilestonesWithTiming(
          userId, 
          data.id, 
          data.name, 
          'immediate',
          { autoGenerated: true }
        );
        console.log('🎓 addCollege: Generated', milestoneResult.total || 0, 'immediate milestone templates');
      } catch (milestoneError) {
        console.error('🎓 addCollege: Error auto-generating immediate milestones:', milestoneError);
        // Don't throw - college addition should succeed even if milestone generation fails
      }
      
      return data;
    } catch (error) {
      console.error('Error adding college:', error);
      throw error;
    }
  },

  // Get available global colleges for selection
  async getAvailableColleges(searchTerm = '', limit = 50) {
    try {
      let query = supabase
        .from('global_colleges')
        .select('id, name, official_name, city, state, location, type')
        .eq('is_active', true)
        .order('name');

      if (searchTerm) {
        query = query.or(
          `name.ilike.%${searchTerm}%,official_name.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%,state.ilike.%${searchTerm}%`
        );
      }

      const { data, error } = await query.limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting available colleges:', error);
      throw error;
    }
  },

  // Submit a request for a new college to be added
  async requestNewCollege(collegeRequestData, userId) {
    try {
      console.log('📝 Requesting new college:', collegeRequestData);

      const insertData = {
        requested_by: userId,
        college_name: collegeRequestData.name,
        location: collegeRequestData.location,
        city: collegeRequestData.city,
        state: collegeRequestData.state,
        website: collegeRequestData.website,
        type: collegeRequestData.type || 'unknown',
        additional_info: collegeRequestData.additionalInfo,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('college_requests')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      
      console.log('✅ College request submitted:', data);
      return data;
    } catch (error) {
      console.error('Error submitting college request:', error);
      throw error;
    }
  },
  
  async updateCollege(collegeId, updates, userId, skipUserCheck) {
    try {
      // ED VALIDATION: Check if trying to update to Early Decision
      if (updates.application_round === 'early_decision') {
        // Get the actual user_id if not provided
        let effectiveUserId = userId;
        if (!effectiveUserId && !skipUserCheck) {
          const { data: college } = await supabase
            .from('college_journey_colleges')
            .select('user_id')
            .eq('id', collegeId)
            .single();

          effectiveUserId = college?.user_id;
        }

        if (effectiveUserId) {
          const validation = await EDValidationService.validateApplicationRound(
            effectiveUserId,
            collegeId,
            'early_decision'
          );

          if (!validation.isValid) {
            console.error('🚫 ED Validation failed:', validation.message);
            throw new Error(validation.message);
          }
        }
      }

      // Define which fields are student-specific and belong in college_journey_colleges
      const studentSpecificFields = [
        'category', 'status', 'notes', 'student_advantages', 
        'application_strategy', 'strategic_classification',
        'recommended_strategy', 'application_status', 'admission_decision',
        'financial_aid_status', 'visit_status', 'interview_status',
        'priority_level', 'parent_notes', 'counselor_notes',
        'why_interested', 'major_of_interest', 'questions_for_college',
        'alumni_connections', 'demonstrated_interest',
        'supplemental_essays_status', 'recommendation_letters_status',
        'application_submitted_date', 'decision_received_date',
        'scholarship_amount', 'net_cost', 'is_attending',
        'application_round', 'visited', 'source',
        'suggestion_source', 'suggested_by', 'suggestion_accepted_at',
        'ai_suggestions', 'ai_suggestions_updated_at'
      ];

      // Define which fields are global college data and belong in global_colleges
      const globalCollegeFields = [
        'name', 'official_name', 'city', 'state', 'type', 'website', 'academic_focus',
        'usnwr_ranking', 'liberal_arts_ranking', 'engineering_ranking', 'nursing_ranking',
        'business_ranking', 'computer_science_ranking', 'acceptance_rate', 'test_optional',
        'test_blind', 'sat_range', 'act_range', 'tuition', 'tuition_in_state', 
        'tuition_out_of_state', 'room_board', 'total_cost', 'need_blind', 'avg_merit_aid',
        'num_essays', 'essay_prompts', 'requires_counselor_rec', 'requires_teacher_rec',
        'num_teacher_recs_required', 'interview_required', 'interview_optional',
        'ed_deadline', 'ed2_deadline', 'ea_deadline', 'rd_deadline', 'finaid_deadline',
        'css_deadline', 'merit_deadline', 'honors_deadline'
      ];
      
      // Separate student-specific fields from global college fields
      const studentUpdates = {};
      const globalUpdates = {};
      
      for (const [key, value] of Object.entries(updates)) {
        if (studentSpecificFields.includes(key)) {
          studentUpdates[key] = value;
        } else if (globalCollegeFields.includes(key)) {
          globalUpdates[key] = value;
        }
      }
      
      // Always update the timestamp for student records
      studentUpdates.updated_at = new Date().toISOString();
      
      console.log('🔄 Student fields to update:', Object.keys(studentUpdates));
      console.log('🌍 Global fields to update:', Object.keys(globalUpdates));
      
      // Get the current college record to find global_college_id
      const { data: currentCollege, error: fetchError } = await supabase
        .from('college_journey_colleges')
        .select('global_college_id')
        .eq('id', collegeId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Update student-specific fields in college_journey_colleges
      const { data: updatedStudentData, error: studentError } = await supabase
        .from('college_journey_colleges')
        .update(studentUpdates)
        .eq('id', collegeId)
        .select()
        .single();
      
      if (studentError) throw studentError;
      
      // Update global college fields if there are any and we have a global_college_id
      if (Object.keys(globalUpdates).length > 0 && currentCollege.global_college_id) {
        globalUpdates.data_last_updated = new Date().toISOString();
        
        const { error: globalError } = await supabase
          .from('global_colleges')
          .update(globalUpdates)
          .eq('id', currentCollege.global_college_id);
        
        if (globalError) {
          console.warn('⚠️ Failed to update global college data:', globalError);
          // Don't throw error - student data was still updated successfully
        } else {
          console.log('✅ Updated global college data for:', currentCollege.global_college_id);
        }
      }
      
      return updatedStudentData;
    } catch (error) {
      console.error('Error updating college:', error);
      throw error;
    }
  },
  
  async removeCollege(collegeId, userId) {
    try {
      // First get the college details for cleanup
      const { data: college, error: fetchError } = await supabase
        .from('college_journey_colleges')
        .select('college_id, name')
        .eq('id', collegeId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Delete associated milestones and tasks
      if (college?.college_id) {
        // Clean up timeline items
        const { error: timelineCleanupError } = await supabase
          .from('timeline_items')
          .delete()
          .eq('user_id', userId)
          .eq('college_id', college.college_id)
          .in('source_type', ['milestone_template', 'college_deadline', 'essay_generation']);
        
        if (timelineCleanupError) {
          console.warn('⚠️ Error cleaning up timeline items:', timelineCleanupError);
        } else {
          console.log('✅ Cleaned up associated timeline items');
        }

        // Clean up generated milestones from milestone-related tables if they exist
        try {
          // Clean up any college-specific milestones that were generated
          const cleanupQueries = [
            // Clean up task audit log entries first (has foreign key to college_journey_colleges.id)
            // IMPORTANT: Use collegeId (college_journey_colleges.id) not college.college_id
            supabase
              .from('task_audit_log')
              .delete()
              .eq('college_id', collegeId),

            // Clean up any tasks that reference this college
            supabase
              .from('tasks')
              .delete()
              .eq('user_id', userId)
              .eq('college_id', college.college_id),

            // Clean up any college-specific essays
            supabase
              .from('college_journey_essays')
              .delete()
              .eq('user_id', userId)
              .eq('college_id', college.college_id),

            // Clean up any college deadlines
            supabase
              .from('college_deadlines')
              .delete()
              .eq('college_id', college.college_id)
              .eq('user_id', userId)
          ];

          await Promise.all(cleanupQueries.map(query =>
            query.then(result => {
              if (result.error && !result.error.message?.includes('does not exist')) {
                console.warn('⚠️ Cleanup warning:', result.error);
              }
              return result;
            })
          ));

          console.log(`✅ Completed milestone cleanup for ${college.name}`);
        } catch (cleanupError) {
          console.warn('⚠️ Error during milestone cleanup:', cleanupError);
          // Continue with college deletion even if cleanup fails
        }
      }

      // Delete the college
      const { error } = await supabase
        .from('college_journey_colleges')
        .delete()
        .eq('id', collegeId);
      
      if (error) throw error;
      
      console.log(`Removed college ${college?.name} and associated milestones`);
      return true;
    } catch (error) {
      console.error('Error removing college:', error);
      throw error;
    }
  },
  
  async updateCollegeStrategy(collegeId, strategyUpdates, userId) {
    try {
      // First, get the current college data to merge ai_suggestions
      const { data: currentCollege, error: fetchError } = await supabase
        .from('college_journey_colleges')
        .select('ai_suggestions')
        .eq('id', collegeId)
        .single();

      if (fetchError) throw fetchError;

      // Update college strategy fields
      const collegeUpdateFields = {
        updated_at: new Date().toISOString()
      };
      
      // Add strategy fields that belong to college record
      if (strategyUpdates.application_strategy) collegeUpdateFields.application_strategy = strategyUpdates.application_strategy;
      if (strategyUpdates.strategic_classification) collegeUpdateFields.strategic_classification = strategyUpdates.strategic_classification;
      if (strategyUpdates.recommended_strategy) collegeUpdateFields.recommended_strategy = strategyUpdates.recommended_strategy;
      if (strategyUpdates.advantages) {
        collegeUpdateFields.student_advantages = strategyUpdates.advantages;
        console.log('💾 Setting student_advantages to:', strategyUpdates.advantages.substring(0, 100) + '...');
      }

      // Handle strategy object fields that go into ai_suggestions
      const strategyFields = ['interests', 'experiences', 'perspectives', 'programs', 'campus_life', 'culture'];
      const hasStrategyFields = strategyFields.some(field => field in strategyUpdates);
      
      if (hasStrategyFields) {
        // Get current ai_suggestions or initialize empty object
        const currentSuggestions = currentCollege?.ai_suggestions || {};
        
        // Update only the provided strategy fields
        strategyFields.forEach(field => {
          if (field in strategyUpdates) {
            currentSuggestions[field] = strategyUpdates[field];
            console.log(`💾 Updating ai_suggestions.${field}:`, strategyUpdates[field]?.substring(0, 50) + '...');
          }
        });
        
        collegeUpdateFields.ai_suggestions = currentSuggestions;
        collegeUpdateFields.ai_suggestions_updated_at = new Date().toISOString();
      }
      
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .update(collegeUpdateFields)
        .eq('id', collegeId)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Updated college strategy:', {
        hasAdvantages: !!strategyUpdates.advantages,
        hasStrategyFields,
        updatedFields: Object.keys(collegeUpdateFields)
      });
      return data;
    } catch (error) {
      console.error('Error updating college strategy:', error);
      throw error;
    }
  },
  
  // Essay management
  async getEssays(userId) {
    try {
      console.log('📝 getEssays: Fetching essays for userId:', userId);
      
      const { data, error } = await supabase
        .from('college_journey_essays')
        .select('*')
        .eq('user_id', userId)
        .or('deleted.is.null,deleted.eq.false')  // Filter out deleted essays
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('📝 getEssays: Error details:', error);
        throw error;
      }
      
      console.log(`📝 getEssays: Found ${data?.length || 0} essays`);
      return data || [];
    } catch (error) {
      console.error('Error getting essays:', error);
      return [];
    }
  },
  
  async addEssay(essayData, userId) {
    try {
      console.log('📝 addEssay: Adding essay for userId:', userId);

      const { data, error } = await supabase
        .from('college_journey_essays')
        .insert({
          user_id: userId,
          title: essayData.title,
          type: essayData.type || 'supplemental',
          status: essayData.status || 'draft',
          content: essayData.content || '',
          notes: essayData.notes || '',
          source: essayData.source || 'manual',
          college_id: essayData.college_id || null,
          word_count: essayData.word_count || 0,
          word_limit: essayData.word_limit || null,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('📝 addEssay: Error details:', error);
        throw error;
      }

      console.log('📝 addEssay: Successfully added essay:', data);

      // Trigger essay task sync in background
      this.triggerEssayTaskSync(userId);

      return data;
    } catch (error) {
      console.error('Error adding essay:', error);
      throw error;
    }
  },
  
  async updateEssay(essayId, updates, userId) {
    try {
      console.log('📝 updateEssay: Updating essay:', essayId, 'for userId:', userId);
      console.log('📝 updateEssay: Updates:', updates);

      const { data, error } = await supabase
        .from('college_journey_essays')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', essayId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('📝 updateEssay: Error details:', error);
        throw error;
      }

      console.log('📝 updateEssay: Successfully updated essay:', data);

      // Trigger essay task sync in background
      this.triggerEssayTaskSync(userId);

      return data;
    } catch (error) {
      console.error('Error updating essay:', error);
      throw error;
    }
  },
  
  async deleteEssay(essayId, force, userId) {
    try {
      console.log('📝 deleteEssay: Deleting essay:', essayId, 'for userId:', userId, 'force:', force);
      
      if (force) {
        // Permanent deletion
        const { error } = await supabase
          .from('college_journey_essays')
          .delete()
          .eq('id', essayId)
          .eq('user_id', userId);
        
        if (error) {
          console.error('📝 deleteEssay: Error permanently deleting:', error);
          throw error;
        }
        
        console.log('📝 deleteEssay: Permanently deleted essay:', essayId);
      } else {
        // Soft delete - just mark as deleted
        const { data, error } = await supabase
          .from('college_journey_essays')
          .update({
            deleted: true,
            deleted_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', essayId)
          .eq('user_id', userId)
          .select()
          .single();
        
        if (error) {
          console.error('📝 deleteEssay: Error soft deleting:', error);
          throw error;
        }
        
        console.log('📝 deleteEssay: Soft deleted essay:', data);
        return data;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting essay:', error);
      throw error;
    }
  },
  
  // Timeline management
  async getTimeline(userId) {
    try {
      console.log('📅 getTimeline: Fetching timeline from timeline_items for userId:', userId);
      
      const { data, error } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .order('due_date', { ascending: true });
      
      if (error) {
        console.error('📅 getTimeline: Error details:', error);
        throw error;
      }
      
      console.log(`📅 getTimeline: Found ${data?.length || 0} timeline items from timeline_items table`);
      return data || [];
    } catch (error) {
      console.error('Error getting timeline:', error);
      return [];
    }
  },
  
  async addTimelineItem(timelineData, userId) {
    try {
      console.log('📅 addTimelineItem: Adding timeline item for userId:', userId);
      console.log('📅 addTimelineItem: Input data:', timelineData);
      
      // Map 'general' and other unsupported categories to 'applications' as a safe default
      let category = timelineData.category;
      const validCategories = ['testing', 'applications', 'essays', 'interviews', 'decisions', 'visits', 'financial'];
      
      if (!category || !validCategories.includes(category)) {
        console.log(`📅 addTimelineItem: Category '${category}' not valid, defaulting to 'applications'`);
        category = 'applications'; // Safe default that exists in the constraint
      }
      
      const insertData = {
        user_id: userId,
        title: timelineData.title,
        description: timelineData.description || '',
        due_date: timelineData.due_date || null,
        category: category,
        priority: timelineData.priority || 'medium',
        status: timelineData.status || 'pending',
        source: timelineData.source || 'manual',
        college_id: timelineData.college_id || null,
        timeline_text: timelineData.timeline_text || null,
        created_at: new Date().toISOString()
      };
      
      console.log('📅 addTimelineItem: Inserting data:', insertData);
      
      const { data, error } = await supabase
        .from('timeline_items')
        .insert(insertData)
        .select()
        .single();
      
      if (error) {
        console.error('📅 addTimelineItem: Error details:', error);
        console.error('📅 addTimelineItem: Failed insert data:', insertData);
        throw error;
      }
      
      console.log('📅 addTimelineItem: Successfully added timeline item:', data);
      return data;
    } catch (error) {
      console.error('Error adding timeline item:', error);
      throw error;
    }
  },
  
  async updateTimelineItem(itemId, updates, userId) {
    // TODO: Implement timeline item update
    return { id: itemId, ...updates };
  },
  
  // Strategy management
  async getMasterStrategy(userId) {
    // TODO: Implement master strategy retrieval
    return {};
  },
  
  async updateMasterStrategy(strategyUpdates, userId) {
    // TODO: Implement master strategy update
    return strategyUpdates;
  },
  
  // Profile management
  async getPersonalProfile(userId) {
    // TODO: Implement personal profile retrieval
    return {};
  },
  
  async updatePersonalProfile(profileUpdates, userId) {
    // TODO: Implement personal profile update
    return profileUpdates;
  },
  
  async getAcademicProfile(userId) {
    try {
      console.log('🔍 getAcademicProfile: Querying for userId:', userId);
      
      // First, let's see all profiles to debug
      const { data: allProfiles } = await supabase
        .from('student_academic_profiles')
        .select('id, user_id, graduation_year, intended_major, gpa_weighted');
      
      console.log('🔍 getAcademicProfile: All profiles:', allProfiles);
      
      // Get all profiles for this user, ordered by most recent and prefer ones with data
      const { data: userProfiles, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });
      
      console.log('🔍 getAcademicProfile: Query result - profiles:', userProfiles, 'error:', error);
      
      if (userProfiles && userProfiles.length > 0) {
        // Prefer profiles with actual data over empty ones
        const profileWithData = userProfiles.find(p => 
          p.academic_interests || p.intended_major || p.gpa_weighted || p.sat_total || p.act_composite
        );
        
        const selectedProfile = profileWithData || userProfiles[0]; // Fall back to most recent
        console.log('🔍 getAcademicProfile: Selected profile:', selectedProfile);
        console.log('🔍 getAcademicProfile: Had', userProfiles.length, 'profiles, chose one with data:', !!profileWithData);
        return selectedProfile;
      }
      
      // If no exact match (either error or no data), try to find any profile (could be a proxy profile)
      console.log('🔍 getAcademicProfile: No specific profile found, checking if we can find any profile...');
      
      const { data: anyProfile, error: anyError } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .maybeSingle();
        
      if (anyProfile) {
        console.log('🔍 getAcademicProfile: Found any profile (possibly proxy):', anyProfile);
        // Debug fields available  
        // console.log('🔍 getAcademicProfile: Available fields:', Object.keys(anyProfile));
        return anyProfile;
      }
      
      if (anyError) {
        console.log('🔍 getAcademicProfile: Any profile query failed:', anyError);
      }
      
      console.log('🔍 getAcademicProfile: No profile found, returning empty object');
      return {};
    } catch (error) {
      console.error('Error getting academic profile:', error);
      return {};
    }
  },
  
  async updateAcademicProfile(profileUpdates, userId) {
    try {
      console.log('🔄 DEBUG: updateAcademicProfile called with:');
      console.log('  - userId:', userId);
      console.log('  - userId type:', typeof userId);
      console.log('  - userId length:', userId?.length);
      console.log('  - profileUpdates keys:', Object.keys(profileUpdates || {}));
      
      // First, verify the user exists in the family_members table to avoid foreign key constraint errors
      const { data: userExists, error: userCheckError } = await supabase
        .from('family_members')
        .select('id')
        .eq('id', userId)
        .single();
      
      console.log('🔄 DEBUG: Family member lookup result:');
      console.log('  - userExists:', userExists);
      console.log('  - userCheckError:', userCheckError);
      
      if (userCheckError || !userExists) {
        console.error('🔄 updateAcademicProfile: User not found in family_members table:', userId, userCheckError);
        // Don't throw error - continue with the update attempt as the user may exist
        console.warn('🔄 updateAcademicProfile: Continuing despite user check failure');
      } else {
        console.log('🔄 DEBUG: User confirmed to exist in family_members table');
      }
      
      // Find the most recent/complete existing profile
      let existingProfile = null;
      
      console.log('🔄 DEBUG: Looking for existing profiles for userId:', userId);
      
      // Try to find the most recent profile for this user_id (order by updated_at DESC)
      const { data: userProfiles, error: profilesError } = await supabase
        .from('student_academic_profiles')
        .select('id, user_id, academic_interests, intended_major, gpa_weighted, updated_at, created_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });
      
      console.log('🔄 DEBUG: Profile lookup result:');
      console.log('  - userProfiles:', userProfiles);
      console.log('  - profilesError:', profilesError);
      console.log('  - Number of profiles found:', userProfiles?.length || 0);
      
      if (userProfiles && userProfiles.length > 0) {
        // Prefer profiles with actual data over empty ones
        const profileWithData = userProfiles.find(p => 
          p.academic_interests || p.intended_major || p.gpa_weighted
        );
        
        existingProfile = profileWithData || userProfiles[0]; // Fall back to most recent if none have data
        console.log('🔄 DEBUG: Selected existing profile:', existingProfile);
        console.log('🔄 updateAcademicProfile: Found user profile:', existingProfile);
        console.log('🔄 updateAcademicProfile: Total profiles found:', userProfiles.length);
      } else {
        console.log('🔄 DEBUG: No user-specific profiles found, trying fallback');
        // Try fallback (proxy profile) only if no user-specific profiles exist
        const { data: anyProfile, error: fallbackError } = await supabase
          .from('student_academic_profiles')
          .select('id, user_id, academic_interests, intended_major, gpa_weighted, updated_at')
          .order('updated_at', { ascending: false })
          .maybeSingle();
        
        console.log('🔄 DEBUG: Fallback profile lookup:');
        console.log('  - anyProfile:', anyProfile);
        console.log('  - fallbackError:', fallbackError);
          
        if (anyProfile) {
          existingProfile = anyProfile;
          console.log('🔄 updateAcademicProfile: Found any profile (proxy):', existingProfile);
        }
      }
      
      console.log('🔄 DEBUG: Decision point - existingProfile:', !!existingProfile);
      if (existingProfile) {
        console.log('🔄 DEBUG: Existing profile details:', {
          id: existingProfile.id,
          user_id: existingProfile.user_id,
          hasData: !!(existingProfile.academic_interests || existingProfile.intended_major || existingProfile.gpa_weighted)
        });
      }
      
      // Safeguard: Don't overwrite rich data with empty/null values
      const payload = {
        updated_at: new Date().toISOString()
      };
      
      // Only include non-empty updates in the payload
      Object.keys(profileUpdates).forEach(key => {
        const value = profileUpdates[key];
        
        // Include the update if:
        // 1. Value is not null/undefined/empty string
        // 2. OR if it's an intentional reset (explicitly set to null/empty)
        // 3. OR if it's a boolean or number (including 0)
        if (value !== null && value !== undefined && value !== '' && value !== '{}' && value !== '[]') {
          payload[key] = value;
        } else if (typeof value === 'boolean' || typeof value === 'number') {
          payload[key] = value;
        } else {
          // For intentional resets, still allow but log it
          console.log(`🔄 updateAcademicProfile: Skipping empty value for ${key}:`, value);
        }
      });
      
      console.log('🔄 DEBUG: Final payload to save:', payload);
      console.log('🔄 DEBUG: Payload has', Object.keys(payload).length, 'fields');
      
      if (existingProfile) {
        console.log('🔄 DEBUG: Taking UPDATE path for existing profile');
        console.log('🔄 updateAcademicProfile: Updating existing profile with ID:', existingProfile.id);
        
        // Double-check that the profile still exists before attempting update
        const { data: stillExists, error: checkError } = await supabase
          .from('student_academic_profiles')
          .select('id')
          .eq('id', existingProfile.id)
          .maybeSingle();
        
        console.log('🔄 DEBUG: Pre-update existence check:');
        console.log('  - stillExists:', stillExists);
        console.log('  - checkError:', checkError);
        
        if (!stillExists) {
          console.log('🔄 DEBUG: Profile no longer exists, switching to CREATE path');
          // Profile was deleted between our queries, create a new one instead
          const insertPayload = {
            user_id: userId,
            ...payload
          };
          
          const { data: newData, error: insertError } = await supabase
            .from('student_academic_profiles')
            .insert(insertPayload)
            .select()
            .single();
          
          if (insertError) {
            console.error('🔄 updateAcademicProfile: Insert error after profile disappeared:', insertError);
            throw insertError;
          }
          
          console.log('🔄 updateAcademicProfile: Created new profile after original disappeared:', newData);
          return newData;
        }
        
        // Update existing profile by ID (not by user_id to handle proxy profiles)
        const { data, error } = await supabase
          .from('student_academic_profiles')
          .update(payload)
          .eq('id', existingProfile.id)
          .select();
        
        console.log('🔄 DEBUG: Update result (before .single()):');
        console.log('  - data array:', data);
        console.log('  - data length:', data?.length);
        console.log('  - error:', error);
        
        if (error) {
          console.error('🔄 updateAcademicProfile: Update error:', error);
          throw error;
        }
        
        if (!data || data.length === 0) {
          console.error('🔄 updateAcademicProfile: Update returned 0 rows - profile may have been deleted');
          throw new Error('Profile was deleted during update operation');
        }
        
        const updatedRecord = data[0]; // Get first record instead of using .single()
        
        console.log('🔄 updateAcademicProfile: Update successful:', updatedRecord);
        return updatedRecord;
      } else {
        console.log('🔄 DEBUG: Taking CREATE path for new profile');
        console.log('🔄 updateAcademicProfile: Creating new profile');
        
        // Create new profile with the requesting user's ID
        const insertPayload = {
          user_id: userId,
          ...payload
        };
        
        console.log('🔄 DEBUG: Insert payload:', insertPayload);
        console.log('🔄 DEBUG: Insert payload user_id:', insertPayload.user_id);
        
        const { data, error } = await supabase
          .from('student_academic_profiles')
          .insert(insertPayload)
          .select()
          .single();
        
        console.log('🔄 DEBUG: Insert result:');
        console.log('  - data:', data);
        console.log('  - error:', error);
        
        if (error) {
          console.error('🔄 updateAcademicProfile: Insert error:', error);
          console.error('🔄 DEBUG: Insert error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        
        console.log('🔄 updateAcademicProfile: Insert successful:', data);
        return data;
      }
    } catch (error) {
      console.error('🔄 updateAcademicProfile: Unexpected error:', error);
      console.error('🔄 updateAcademicProfile: Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      throw error;
    }
  },
  
  // Helper function to convert legacy text fields to activity array format
  _convertLegacyActivitiesToArray(profile) {
    const activities = [];
    let id = 1;

    // Convert leadership_activities
    if (profile.leadership_activities && profile.leadership_activities.trim()) {
      activities.push({
        id: `legacy_leadership_${id++}`,
        name: profile.leadership_activities,
        description: '',
        category: 'leadership',
        years_participated: '',
        hours_per_week: null
      });
    }

    // Convert volunteer_work
    if (profile.volunteer_work && profile.volunteer_work.trim()) {
      // Split by comma if multiple activities
      const volunteerActivities = profile.volunteer_work.split(',').map(v => v.trim()).filter(v => v);
      volunteerActivities.forEach(activity => {
        activities.push({
          id: `legacy_volunteer_${id++}`,
          name: activity,
          description: '',
          category: 'community_service',
          years_participated: '',
          hours_per_week: null
        });
      });
    }

    // Convert work_experience
    if (profile.work_experience && profile.work_experience.trim()) {
      activities.push({
        id: `legacy_work_${id++}`,
        name: profile.work_experience,
        description: '',
        category: 'work',
        years_participated: '',
        hours_per_week: null
      });
    }

    // console.log('🔍 _convertLegacyActivitiesToArray: Converted activities:', activities);
    return activities;
  },

  // Helper function to convert legacy text fields to awards array format
  _convertLegacyAwardsToArray(profile) {
    const awards = [];
    let id = 1;

    // Convert academic_awards
    if (profile.academic_awards && profile.academic_awards.trim()) {
      // Split by comma if multiple awards
      const academicAwards = profile.academic_awards.split(',').map(a => a.trim()).filter(a => a);
      academicAwards.forEach(award => {
        awards.push({
          id: `legacy_academic_${id++}`,
          name: award,
          description: '',
          level: 'school',
          date_received: null
        });
      });
    }


    console.log('🔍 _convertLegacyAwardsToArray: Converted awards:', awards);
    return awards;
  },

  // Activities and honors - stored in academic profile table
  async getStudentActivities(userId) {
    try {
      console.log('🔍 getStudentActivities: Querying for userId:', userId);
      
      // Get activities from the academic profile table
      const profile = await this.getAcademicProfile(userId);
      
      console.log('🔍 getStudentActivities: Profile found:', profile);
      
      // Check if we have JSONB activities first
      if (profile?.extracurriculars && Array.isArray(profile.extracurriculars) && profile.extracurriculars.length > 0) {
        console.log('🔍 getStudentActivities: Using JSONB extracurriculars:', profile.extracurriculars);
        return profile.extracurriculars;
      }
      
      // Fall back to legacy text fields and convert them
      const legacyActivities = this._convertLegacyActivitiesToArray(profile || {});
      console.log('🔍 getStudentActivities: Using legacy fields, converted to:', legacyActivities);
      return legacyActivities;
    } catch (error) {
      console.error('Error getting student activities:', error);
      return [];
    }
  },
  
  async updateStudentActivities(activities, userId) {
    try {
      console.log('🔍 updateStudentActivities: Updating activities for userId:', userId);
      
      // Update the extracurriculars field in the academic profile
      await this.updateAcademicProfile({ extracurriculars: activities }, userId);
      
      return activities;
    } catch (error) {
      console.error('Error updating student activities:', error);
      throw error;
    }
  },
  
  async getStudentHonors(userId) {
    try {
      console.log('🔍 getStudentHonors: Querying for userId:', userId);
      
      // Get honors from the academic profile table
      const profile = await this.getAcademicProfile(userId);
      
      console.log('🔍 getStudentHonors: Profile found:', profile);
      
      // Check if we have JSONB awards first
      if (profile?.awards && Array.isArray(profile.awards) && profile.awards.length > 0) {
        console.log('🔍 getStudentHonors: Using JSONB awards:', profile.awards);
        return profile.awards;
      }
      
      // Fall back to legacy text fields and convert them
      const legacyAwards = this._convertLegacyAwardsToArray(profile || {});
      console.log('🔍 getStudentHonors: Using legacy fields, converted to:', legacyAwards);
      return legacyAwards;
    } catch (error) {
      console.error('Error getting student honors:', error);
      return [];
    }
  },
  
  async updateStudentHonors(honors, userId) {
    try {
      console.log('🔍 updateStudentHonors: Updating honors for userId:', userId);
      
      // Update the awards field in the academic profile
      await this.updateAcademicProfile({ awards: honors }, userId);
      
      return honors;
    } catch (error) {
      console.error('Error updating student honors:', error);
      throw error;
    }
  },
  
  // Free writes
  async getFreeWrites(userId) {
    try {
      console.log('📝 getFreeWrites: Fetching free writes for userId:', userId);
      
      const { data, error } = await supabase
        .from('college_journey_freewrites')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });
      
      if (error) {
        console.error('📝 getFreeWrites: Error fetching free writes:', error);
        throw error;
      }
      
      console.log('📝 getFreeWrites: Found', data?.length || 0, 'free writes');
      return data || [];
    } catch (error) {
      console.error('Error fetching free writes:', error);
      // Return empty array on error to prevent app crash
      return [];
    }
  },
  
  async getOrCreateFreeWrite(date, userId = null) {
    try {
      console.log('📝 getOrCreateFreeWrite: Looking for free write on', date, 'for userId:', userId);
      
      // First, try to fetch existing record
      const { data: existing, error: fetchError } = await supabase
        .from('college_journey_freewrites')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .single();
      
      // If we found an existing record, return it (preserving its content)
      if (existing) {
        console.log('📝 getOrCreateFreeWrite: Found existing free write:', existing.id, 'with content length:', existing.content?.length || 0);
        return existing;
      }
      
      // If error is not "no rows" error, throw it
      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('📝 getOrCreateFreeWrite: Error fetching:', fetchError);
        throw fetchError;
      }
      
      // No existing record found, create a new one with empty content
      console.log('📝 getOrCreateFreeWrite: Creating new free write for', date);
      
      const newFreeWriteData = {
        user_id: userId,
        date: date,
        content: '',
        word_count: 0,
        created_at: new Date().toISOString(),
        last_modified: new Date().toISOString()
      };
      
      const { data: newFreeWrite, error: createError } = await supabase
        .from('college_journey_freewrites')
        .insert(newFreeWriteData)
        .select()
        .single();
      
      if (createError) {
        // Handle race condition: if another process created it between our check and insert
        if (createError.code === '23505') {
          console.log('📝 getOrCreateFreeWrite: Race condition detected, fetching existing record');
          const { data: raceFallback, error: raceError } = await supabase
            .from('college_journey_freewrites')
            .select('*')
            .eq('user_id', userId)
            .eq('date', date)
            .single();
          
          if (raceError) {
            console.error('📝 getOrCreateFreeWrite: Error in race condition fallback:', raceError);
            throw raceError;
          }
          
          if (raceFallback) {
            console.log('📝 getOrCreateFreeWrite: Retrieved existing record after race condition:', raceFallback.id);
            return raceFallback;
          }
        }
        
        console.error('📝 getOrCreateFreeWrite: Error creating:', createError);
        throw createError;
      }
      
      console.log('📝 getOrCreateFreeWrite: Successfully created new free write:', newFreeWrite.id);
      return newFreeWrite;
    } catch (error) {
      console.error('Error in getOrCreateFreeWrite:', error);
      throw error;
    }
  },
  
  async updateFreeWrite(freeWriteId, content, userId) {
    try {
      console.log('📝 updateFreeWrite: Updating free write:', freeWriteId, 'for userId:', userId);
      
      const wordCount = content.trim().split(/\s+/).filter(word => word.length > 0).length;
      
      const { data, error } = await supabase
        .from('college_journey_freewrites')
        .update({
          content: content,
          word_count: wordCount,
          last_modified: new Date().toISOString()
        })
        .eq('id', freeWriteId)
        .eq('user_id', userId)
        .select()
        .single();
      
      if (error) {
        console.error('📝 updateFreeWrite: Error details:', error);
        throw error;
      }
      
      console.log('📝 updateFreeWrite: Successfully updated free write:', data);
      return data;
    } catch (error) {
      console.error('Error updating free write:', error);
      throw error;
    }
  },
  
  // Helper function to create sample colleges for testing
  async createSampleColleges(userId, addedByUserId) {
    try {
      console.log('🎓 Creating sample colleges for testing...');
      
      const sampleColleges = [
        {
          name: 'Stanford University',
          location: 'Stanford, CA',
          category: 'reach',
          acceptance_rate: 4.3,
          tuition_in_state: 56169,
          tuition_out_state: 56169,
          enrollment_total: 17246,
          sat_25th: 1420,
          sat_75th: 1570,
          act_25th: 31,
          act_75th: 35,
          user_id: userId,
          suggested_by: addedByUserId || userId,
          suggestion_source: addedByUserId && addedByUserId !== userId ? 'parent' : 'student'
        },
        {
          name: 'UC Berkeley',
          location: 'Berkeley, CA',
          category: 'target',
          acceptance_rate: 14.5,
          tuition_in_state: 14226,
          tuition_out_state: 44008,
          enrollment_total: 45057,
          sat_25th: 1330,
          sat_75th: 1530,
          act_25th: 29,
          act_75th: 35,
          user_id: userId,
          suggested_by: addedByUserId || userId,
          suggestion_source: addedByUserId && addedByUserId !== userId ? 'parent' : 'student'
        },
        {
          name: 'San Jose State University',
          location: 'San Jose, CA',
          category: 'safety',
          acceptance_rate: 67.4,
          tuition_in_state: 7852,
          tuition_out_state: 19732,
          enrollment_total: 36208,
          sat_25th: 1040,
          sat_75th: 1270,
          act_25th: 20,
          act_75th: 27,
          user_id: userId,
          suggested_by: addedByUserId || userId,
          suggestion_source: addedByUserId && addedByUserId !== userId ? 'parent' : 'student'
        }
      ];
      
      const results = [];
      for (const college of sampleColleges) {
        try {
          const result = await this.addCollege(college, userId);
          results.push(result);
          console.log('✅ Added sample college:', college.name);
        } catch (error) {
          console.error('❌ Failed to add sample college:', college.name, error);
        }
      }
      
      console.log('🎓 Sample colleges created:', results.length);
      return results;
    } catch (error) {
      console.error('Error creating sample colleges:', error);
      return [];
    }
  },

  // Authentication synchronization function
  async synchronizeAuthentication() {
    try {
      console.log('🔧 synchronizeAuthentication: Starting authentication sync...');
      
      // Get current Supabase session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('🔧 synchronizeAuthentication: Session error:', sessionError);
        return { success: false, error: sessionError.message };
      }
      
      if (!session?.user?.id) {
        console.log('🔧 synchronizeAuthentication: No authenticated user found');
        return { success: false, error: 'No authenticated user' };
      }
      
      console.log('🔧 synchronizeAuthentication: Found authenticated user:', session.user.id);
      
      // Check if user exists in family_members
      const { data: familyMember, error: memberError } = await supabase
        .from('family_members')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();
      
      if (memberError) {
        console.error('🔧 synchronizeAuthentication: Error checking family membership:', memberError);
        return { success: false, error: memberError.message };
      }
      
      if (familyMember) {
        console.log('🔧 synchronizeAuthentication: User already in family_members');
        return { 
          success: true, 
          userInFamily: true, 
          familyMember,
          message: 'User already authenticated and in family' 
        };
      }
      
      // User not in family_members, need to add them
      console.log('🔧 synchronizeAuthentication: Adding user to family_members...');
      
      // Add user directly to family_members table
      const { data: newMember, error: insertError } = await supabase
        .from('family_members')
        .insert({
          name: session.user.email || 'Auto-added User',
          auth_user_id: session.user.id,
          role: 'parent',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (insertError) {
        console.error('🔧 synchronizeAuthentication: Error adding to family:', insertError);
        return { success: false, error: insertError.message };
      }
      
      console.log('🔧 synchronizeAuthentication: Successfully added user to family:', newMember);
      
      // Test college access
      const colleges = await this.getColleges(session.user.id);
      
      return { 
        success: true, 
        userInFamily: true,
        newMember,
        collegeCount: colleges?.length || 0,
        message: `Successfully added user to family. ${colleges?.length || 0} colleges accessible.`
      };
      
    } catch (error) {
      console.error('🔧 synchronizeAuthentication: Unexpected error:', error);
      return { success: false, error: error.message };
    }
  },

  // Debug function to check database connectivity and contents
  async debugCollegeDatabase() {
    try {
      console.log('🔍 DEBUG: Checking college database...');
      
      // Get current session first
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      console.log('🔍 DEBUG: Current session user:', currentSession?.user?.id);
      
      // Test 1: Check table exists and basic connectivity
      const { data: testQuery, error: testError } = await supabase
        .from('college_journey_colleges')
        .select('count')
        .limit(1);
      
      if (testError) {
        console.error('🔍 DEBUG: Database connectivity error:', testError);
        console.error('🔍 DEBUG: Full error details:', testError);
        return { error: testError, hasData: false };
      }
      
      // Test 2: Get total count without filtering
      const { count: totalCount, error: countError } = await supabase
        .from('college_journey_colleges')
        .select('*', { count: 'exact', head: true });
      
      console.log('🔍 DEBUG: Total colleges in database (with RLS):', totalCount);
      
      // Test 3: Try to get a few sample records
      const { data: sampleData, error: sampleError } = await supabase
        .from('college_journey_colleges')
        .select('id, name, user_id, suggested_by, created_at')
        .limit(5);
      
      console.log('🔍 DEBUG: Sample colleges accessible:', sampleData?.length || 0);
      if (sampleData && sampleData.length > 0) {
        console.log('🔍 DEBUG: Sample records:', sampleData);
      }
      
      // Test 4: Check if current user is in family_members
      const { data: familyMember, error: familyMemberError } = await supabase
        .from('family_members')
        .select('*')
        .eq('auth_user_id', currentSession?.user?.id)
        .maybeSingle();
      
      console.log('🔍 DEBUG: Current user in family_members:', !!familyMember);
      if (familyMemberError) {
        console.log('🔍 DEBUG: Family member check error:', familyMemberError.message);
      }
      
      // Test 5: Try querying by suggested_by
      const { data: addedByData, error: addedByError } = await supabase
        .from('college_journey_colleges')
        .select('id, name, user_id, suggested_by')
        .eq('suggested_by', currentSession?.user?.id)
        .limit(5);
      
      console.log('🔍 DEBUG: Colleges added by current auth user:', addedByData?.length || 0);
      if (addedByData && addedByData.length > 0) {
        console.log('🔍 DEBUG: Sample added-by records:', addedByData);
      }
      
      // Test 6: Test specific user IDs from logs
      const testUserIds = [
        '3cb82aa8-71ef-45c5-a5e1-1052aef3190e', // Sheppard family member ID
        'd9ed0488-0678-4fe5-b77a-a1387ca8ed1f', // Sheppard auth user ID from logs
        'f6115db5-9ef1-4208-ac60-2bc30a7a91a2', // Parent user ID from logs
        'f25d7879-8ecb-4d99-b151-391635d933b6'  // suggested_by from data (authenticated user)
      ];
      
      for (const userId of testUserIds) {
        const { data: userColleges, error: userError } = await supabase
          .from('college_journey_colleges')
          .select('id, name, user_id, suggested_by')
          .eq('user_id', userId)
          .limit(2);
        
        console.log(`🔍 DEBUG: Colleges for user ${userId}:`, userColleges?.length || 0);
        if (userError) {
          console.log(`🔍 DEBUG: Error for user ${userId}:`, userError.message);
        }
      }
      
      return {
        currentUserId: currentSession?.user?.id,
        userInFamilyMembers: !!familyMember,
        totalCount,
        sampleCount: sampleData?.length || 0,
        hasData: (sampleData?.length || 0) > 0,
        sampleData
      };
    } catch (error) {
      console.error('🔍 DEBUG: Error checking database:', error);
      return { error, hasData: false };
    }
  },

  // Assessment responses
  async getSavedAssessmentResponses(userId) {
    try {
      console.log('🔍 getSavedAssessmentResponses: Querying for userId:', userId);
      
      const { data, error } = await supabase
        .from('assessment_responses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error loading saved assessment responses:', error);
        return null;
      }
      
      const latestResponse = data?.[0] || null;
      console.log('🔍 getSavedAssessmentResponses: Found', data?.length || 0, 'responses, latest:', latestResponse);
      return latestResponse;
    } catch (error) {
      console.error('Error getting saved assessment responses:', error);
      return null;
    }
  },

  async saveAssessmentResponses(userId, responseData) {
    try {
      console.log('💾 saveAssessmentResponses: Saving for userId:', userId);
      
      const { data, error } = await supabase
        .from('assessment_responses')
        .upsert({
          user_id: userId,
          responses: responseData.responses || [],
          pattern_scores: responseData.patternScores || {},
          insights: responseData.insights || {},
          conversation_history: responseData.conversationHistory || [],
          response_set_name: responseData.responseSetName || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;
      
      console.log('💾 saveAssessmentResponses: Saved successfully');
      return data;
    } catch (error) {
      console.error('Error saving assessment responses:', error);
      throw error;
    }
  },

  // Interview Results (Hidden Competencies & Character Identity)
  async saveInterviewResults(userId, interviewType, results) {
    try {
      console.log('💾 saveInterviewResults: Saving interview results as essay ideas');
      console.log('  - userId:', userId);
      console.log('  - interviewType:', interviewType);
      console.log('  - results keys:', results ? Object.keys(results) : []);

      // Validate the input
      if (!userId || !interviewType || !results) {
        throw new Error('Missing required parameters for saveInterviewResults');
      }

      // Map interview types to consistent values for source_type
      const typeMapping = {
        'hidden-competencies': 'hidden_competencies',
        'skills_discovery': 'hidden_competencies',
        'character-identity': 'character_identity',
        'character_identity': 'character_identity',
        'strategic-assessment': 'strategic_assessment'
      };

      const sourceType = typeMapping[interviewType] || interviewType;

      // Extract essay topics from results
      const essayTopics = results.essayTopics || [];

      if (essayTopics.length === 0) {
        console.log('⚠️ No essay topics found in results, storing raw data');
        // Store the full results as a single entry for reference
        const { data, error } = await supabase
          .from('college_journey_essay_ideas')
          .insert({
            user_id: userId,
            source_type: sourceType,
            title: `${sourceType.replace('_', ' ')} Interview Results`,
            description: results.magneticHook || results.surpriseInsight || 'Interview completed',
            content: JSON.stringify(results, null, 2),
            interview_data: results,
            category: 'interview-raw',
            tags: [sourceType, 'interview']
          })
          .select();

        if (error) {
          // Check if it's a column doesn't exist error
          if (error.message?.includes('column') || error.code === '42703') {
            console.error('⚠️ Database migration needed! Run: npx supabase db push');
            console.error('   Migration file: supabase/migrations/20251022211500_enhance_essay_ideas_for_interviews.sql');
          }
          throw error;
        }
        console.log('✅ Saved interview results as raw data');
        return data;
      }

      // Convert each essay topic into an essay idea entry
      const essayIdeas = essayTopics.map(topic => ({
        user_id: userId,
        source_type: sourceType,
        title: topic.title,
        topic: topic.title, // For backward compatibility
        description: topic.story,
        content: topic.whyItWorks || topic.differentiator || '',
        essay_angle: topic.essayAngle,
        narrative_arc: topic.narrativeArc,
        source_quote: topic.sourceQuote,
        why_it_works: topic.whyItWorks,
        differentiator: topic.differentiator,
        potential_prompts: topic.potentialPrompts || [],
        interview_data: {
          ...results,
          specificTopic: topic // Store the specific topic data with full context
        },
        category: 'interview-generated',
        tags: [sourceType, 'interview', ...(topic.potentialPrompts || [])]
      }));

      console.log(`💾 Inserting ${essayIdeas.length} essay ideas from interview`);

      // Insert all essay ideas
      const { data, error } = await supabase
        .from('college_journey_essay_ideas')
        .insert(essayIdeas)
        .select();

      if (error) {
        console.error('❌ Error saving essay ideas:', error);

        // Check if it's a column doesn't exist error
        if (error.message?.includes('column') || error.code === '42703') {
          console.error('⚠️ Database migration needed! Run: npx supabase db push');
          console.error('   Migration file: supabase/migrations/20251022211500_enhance_essay_ideas_for_interviews.sql');
        }

        throw error;
      }

      console.log(`✅ Successfully saved ${data.length} essay ideas from ${sourceType} interview`);
      return data;

    } catch (error) {
      console.error('❌ saveInterviewResults: Error saving interview results:', error);
      console.error('  - Error details:', {
        message: error.message || 'No error message available',
        code: error.code || 'No error code available',
        details: error.details || 'No error details available',
        type: typeof error,
        errorObject: error
      });
      // Don't throw the error, return null to allow the UI to continue
      return null;
    }
  },

  async getInterviewResults(userId, interviewType = null) {
    try {
      console.log('🔍 getInterviewResults: Fetching interview results');
      console.log('  - userId:', userId);
      console.log('  - interviewType:', interviewType);
      
      // Build the query
      let query = supabase
        .from('college_journey_interview_results')
        .select('*')
        .eq('user_id', userId);
      
      // Add interview type filter if specified
      if (interviewType) {
        // Map interview types to consistent values
        const typeMapping = {
          'hidden-competencies': 'hidden_competencies',
          'skills_discovery': 'hidden_competencies',
          'character-identity': 'character_identity',
          'character_identity': 'character_identity'
        };
        
        const normalizedType = typeMapping[interviewType] || interviewType;
        query = query.eq('interview_type', normalizedType);
      }
      
      // Order by completed_at descending to get most recent first
      query = query.order('completed_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        const errorMessage = error.message || '';
        const errorCode = error.code || '';
        
        // If table doesn't exist, return empty array instead of throwing
        if (errorCode === '42P01' || errorMessage.includes('relation') || errorMessage.includes('does not exist')) {
          console.log('⚠️ getInterviewResults: Table does not exist yet, returning empty results');
          return interviewType ? null : [];
        }
        throw error;
      }
      
      // If looking for a specific interview type, return the first (most recent) result
      if (interviewType && data && data.length > 0) {
        console.log('✅ getInterviewResults: Found interview result for type:', interviewType);
        return data[0];
      }
      
      console.log('✅ getInterviewResults: Found', data?.length || 0, 'interview results');
      return data || [];
      
    } catch (error) {
      console.error('❌ getInterviewResults: Error fetching interview results:', error);
      // Return empty results instead of throwing to prevent UI crashes
      return interviewType ? null : [];
    }
  },

  // Deadlines
  async getCollegeDeadlines(collegeId) {
    try {
      console.log('📅 Getting deadlines for college:', collegeId);
      
      // Get the college record to find the global_college_id and application_round
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          name,
          user_id,
          global_college_id,
          application_round
        `)
        .eq('id', collegeId)
        .single();
      
      if (collegeError) {
        console.error('Error fetching college for deadlines:', collegeError);
        return [];
      }
      
      if (!college) {
        console.log('No college found for deadlines');
        return [];
      }
      
      // Get global college data for authoritative deadlines
      let globalCollegeData = null;
      if (college.global_college_id) {
        const { data: globalData, error: globalError } = await supabase
          .from('global_colleges')
          .select(`
            name,
            ed_deadline,
            ed2_deadline,
            ea_deadline,
            rd_deadline,
            finaid_deadline,
            css_deadline,
            merit_deadline,
            honors_deadline
          `)
          .eq('id', college.global_college_id)
          .single();
        
        if (!globalError && globalData) {
          globalCollegeData = globalData;
          console.log('📊 Using global college data as primary source for deadlines');
        }
      }
      
      const deadlines = [];
      
      // Define deadline fields to extract
      const deadlineFields = [
        { field: 'ed_deadline', title: 'Early Decision Deadline', type: 'application', category: 'early_decision' },
        { field: 'ed2_deadline', title: 'Early Decision II Deadline', type: 'application', category: 'early_decision_2' },
        { field: 'ea_deadline', title: 'Early Action Deadline', type: 'application', category: 'early_action' },
        { field: 'rd_deadline', title: 'Regular Decision Deadline', type: 'application', category: 'regular_decision' },
        { field: 'finaid_deadline', title: 'Financial Aid Deadline', type: 'financial_aid', category: 'financial_aid' },
        { field: 'css_deadline', title: 'CSS Profile Deadline', type: 'financial_aid', category: 'css_profile' },
        { field: 'merit_deadline', title: 'Merit Scholarship Deadline', type: 'scholarship', category: 'merit_scholarship' },
        { field: 'honors_deadline', title: 'Honors Program Deadline', type: 'application', category: 'honors_program' }
      ];
      
      // Add fields that only exist in college_journey_colleges
      if (!globalCollegeData) {
        deadlineFields.push(
          { field: 'early_deadline', title: 'Early Application Deadline', type: 'application', category: 'early_application' },
          { field: 'deadline', title: 'Application Deadline', type: 'application', category: 'general_application' },
          { field: 'decision_date', title: 'Decision Release Date', type: 'notification', category: 'decision_notification' }
        );
      }
      
      // Extract deadlines with priority: global_colleges > college_journey_colleges
      deadlineFields.forEach(({ field, title, type, category }) => {
        // Prioritize global college data, fallback to journey college data
        const deadlineValue = globalCollegeData?.[field] || college[field];
        const source = globalCollegeData?.[field] ? 'global_authoritative' : 'student_specific';

        if (deadlineValue) {
          deadlines.push({
            id: `${collegeId}-${field}`,
            college_id: collegeId,
            college_name: college.name,
            title: title,
            description: `${title} for ${college.name}`,
            date: deadlineValue, // Add 'date' field for compatibility
            due_date: deadlineValue,
            deadline_type: type, // Add deadline_type field that CollegeJourneyHubV2 expects
            type: type,
            category: category,
            source: source,
            isGlobal: false
          });
        }
      });
      
      // Also get general deadlines that apply to all students
      // These are stored in college_journey_global_deadlines
      const { data: globalDeadlines, error: globalError } = await supabase
        .from('college_journey_global_deadlines')
        .select('*')
        .eq('user_id', college.user_id) // Only get deadlines for this student
        .order('due_date', { ascending: true });
      
      if (!globalError && globalDeadlines) {
        // Add global deadlines with a source indicator
        globalDeadlines.forEach(deadline => {
          deadlines.push({
            ...deadline,
            source: 'global',
            college_id: collegeId, // Associate with current college for context
            college_name: college.name,
            isGlobal: true
          });
        });
      }
      
      // Sort all deadlines by due date
      deadlines.sort((a, b) => {
        const dateA = new Date(a.due_date);
        const dateB = new Date(b.due_date);
        return dateA - dateB;
      });
      
      console.log(`📅 Found ${deadlines.length} deadlines for college ${college.name}`);
      return deadlines;
      
    } catch (error) {
      console.error('Error getting college deadlines:', error);
      return [];
    }
  },
  
  async addCollegeDeadline(deadlineData, collegeId) {
    // TODO: Implement deadline addition
    return deadlineData;
  },
  
  async updateCollegeDeadline(deadlineId, updates) {
    // TODO: Implement deadline update
    return { id: deadlineId, ...updates };
  },
  
  async deleteCollegeDeadline(deadlineId) {
    // TODO: Implement deadline deletion
    return true;
  },
  
  // Priorities
  async getCollegePriorities(collegeId) {
    // TODO: Implement priorities retrieval
    return [];
  },

  // Parent suggestions (now consolidated in main college_journey_colleges table)
  async getParentSuggestions(childId) {
    try {
      console.log('🔍 getParentSuggestions: Querying for childId:', childId);
      
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('user_id', childId)
        .eq('suggestion_source', 'parent')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      console.log('🔍 getParentSuggestions: Found', data?.length || 0, 'parent suggestions for child:', childId);
      return data || [];
    } catch (error) {
      console.error('Error getting parent suggestions:', error);
      return [];
    }
  },

  // Add a college suggestion from parent/counselor
  async addCollegeSuggestion(collegeData, childId, suggestedBy, suggestionSource = 'parent') {
    try {
      console.log('👨‍👩‍👧‍👦 Adding college suggestion:', collegeData.name, 'for child:', childId, 'by:', suggestedBy);
      
      // Check if this college is already in the child's list
      const { data: existing } = await supabase
        .from('college_journey_colleges')
        .select('id')
        .eq('user_id', childId)
        .eq('global_college_id', collegeData.global_college_id)
        .single();
        
      if (existing) {
        throw new Error('College already in student\'s list');
      }
      
      const suggestionData = {
        user_id: childId,
        global_college_id: collegeData.global_college_id,
        name: collegeData.name,
        category: collegeData.category || 'target',
        status: 'suggested',
        suggestion_source: suggestionSource,
        suggested_by: suggestedBy,
        notes: collegeData.notes || '',
        why_interested: collegeData.why_interested || '',
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .insert(suggestionData)
        .select()
        .single();
        
      if (error) throw error;
      
      console.log('✅ College suggestion added successfully');
      return data;
    } catch (error) {
      console.error('Error adding college suggestion:', error);
      throw error;
    }
  },

  // Accept a college suggestion (change status from 'suggested' to 'interested')
  async acceptCollegeSuggestion(collegeId, userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .update({ 
          status: 'interested',
          updated_at: new Date().toISOString()
        })
        .eq('id', collegeId)
        .eq('user_id', userId)
        .eq('status', 'suggested')
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error accepting college suggestion:', error);
      throw error;
    }
  },
  
  // Main data retrieval - single family architecture: get all colleges
  async getFamilyCollegeJourneyData() {
    try {
      console.log('🔍 getFamilyCollegeJourneyData: Loading all family college data');
      
      // Debug current Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔍 getFamilyCollegeJourneyData: Current auth session:', {
        user_id: session?.user?.id,
        email: session?.user?.email
      });
      
      // Debug database access to identify RLS issues
      const debugInfo = await this.debugCollegeDatabase();
      console.log('🔍 getFamilyCollegeJourneyData: Debug info:', debugInfo);
      
      // Get all colleges - try both approaches for single family architecture
      // Approach 1: All colleges (might be blocked by RLS)
      const { data: allColleges, error: allError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .order('created_at', { ascending: false });
      
      console.log('🔍 getFamilyCollegeJourneyData: All colleges query:', allColleges?.length || 0, 'Error:', allError?.message);
      
      // Approach 2: Colleges where current user is the suggested_by (parent can see children's colleges they suggested)
      const { data: parentAddedColleges, error: parentError } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('suggested_by', session?.user?.id)
        .order('created_at', { ascending: false });
      
      console.log('🔍 getFamilyCollegeJourneyData: Parent-added colleges:', parentAddedColleges?.length || 0, 'Error:', parentError?.message);
      
      // Use whichever approach returned data
      const colleges = (allColleges?.length > 0) ? allColleges : parentAddedColleges;
      const collegeError = (allColleges?.length > 0) ? allError : parentError;
      
      if (collegeError) {
        console.error('🔍 getFamilyCollegeJourneyData: Error fetching colleges:', collegeError);
        console.error('🔍 getFamilyCollegeJourneyData: Error details:', {
          message: collegeError.message,
          code: collegeError.code,
          details: collegeError.details,
          hint: collegeError.hint
        });
      }
      
      console.log('🔍 getFamilyCollegeJourneyData: Found', colleges?.length || 0, 'total colleges');
      if (colleges && colleges.length > 0) {
        console.log('🔍 getFamilyCollegeJourneyData: Sample college:', colleges[0]);
        console.log('🔍 getFamilyCollegeJourneyData: User IDs in colleges:', [...new Set(colleges.map(c => c.user_id))]);
      }
      
      // TODO: Implement other data retrieval (essays, timeline, etc.)
      return {
        colleges: colleges || [],
        essays: [],
        timeline: [],
        masterStrategy: {},
        freeWrites: []
      };
    } catch (error) {
      console.error('Error getting family college journey data:', error);
      return {
        colleges: [],
        essays: [],
        timeline: [],
        masterStrategy: {},
        freeWrites: []
      };
    }
  },

  // Parent suggestion methods
  async addParentSuggestedCollege(collegeData, parentUserId, childUserId, suggestionNote = null) {
    try {
      console.log('👨‍👩‍👧‍👦 Adding parent-suggested college:', {
        college: collegeData.name,
        parentUserId,
        childUserId,
        
      });

      // Add college directly to the child's list with parent attribution
      const result = await this.addCollege({
        ...collegeData,
        status: 'parent_suggested',
        suggested_by: parentUserId,
        parent_suggestion_note: suggestionNote
      }, childUserId);

      console.log('✅ Parent-suggested college added successfully');
      return result;
    } catch (error) {
      console.error('❌ Error adding parent-suggested college:', error);
      throw error;
    }
  },

  // Global college management and merge functionality
  async getGlobalColleges(limit = 100) {
    try {
      console.log('🌍 getGlobalColleges: Fetching global colleges');
      
      const { data, error } = await supabase
        .from('global_colleges')
        .select('*')
        .order('name', { ascending: true })
        .limit(limit);
      
      if (error) throw error;
      
      console.log('🌍 getGlobalColleges: Found', data?.length || 0, 'global colleges');
      return data || [];
    } catch (error) {
      console.error('Error getting global colleges:', error);
      return [];
    }
  },

  async findDuplicateGlobalColleges() {
    try {
      console.log('🔍 findDuplicateGlobalColleges: Searching for duplicates');
      
      // Get all global colleges
      const allColleges = await this.getGlobalColleges(1000);
      
      // Normalize college name for better matching
      const normalizeCollegeName = (name) => {
        return name
          .toLowerCase()
          .replace(/\s*(university|college|school|institute|of|the)\s*/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      };
      
      // Group colleges by normalized name
      const groups = {};
      allColleges.forEach(college => {
        const normalized = normalizeCollegeName(college.name);
        if (!groups[normalized]) {
          groups[normalized] = [];
        }
        groups[normalized].push(college);
      });
      
      // Find groups with duplicates
      const duplicates = Object.values(groups).filter(group => group.length > 1);
      
      console.log('🔍 findDuplicateGlobalColleges: Found', duplicates.length, 'duplicate groups');
      duplicates.forEach((group, index) => {
        console.log(`🔍 Duplicate group ${index + 1}:`, group.map(c => c.name));
      });
      
      return duplicates;
    } catch (error) {
      console.error('Error finding duplicate global colleges:', error);
      return [];
    }
  },

  async mergeGlobalColleges(keepCollegeId, mergeCollegeIds) {
    try {
      console.log('🔄 mergeGlobalColleges: Merging colleges', { keepCollegeId, mergeCollegeIds });
      
      // Start transaction-style operations
      // 1. Get the college we're keeping
      const { data: keepCollege, error: keepError } = await supabase
        .from('global_colleges')
        .select('*')
        .eq('id', keepCollegeId)
        .single();
      
      if (keepError) throw keepError;
      if (!keepCollege) throw new Error('College to keep not found');
      
      // 2. Get colleges we're merging
      const { data: mergeColleges, error: mergeError } = await supabase
        .from('global_colleges')
        .select('*')
        .in('id', mergeCollegeIds);
      
      if (mergeError) throw mergeError;
      
      console.log('🔄 mergeGlobalColleges: Merging', mergeColleges.length, 'colleges into', keepCollege.name);
      
      // 3. Update all college_journey_colleges that reference the merge colleges
      for (const mergeCollege of mergeColleges) {
        const { error: updateError } = await supabase
          .from('college_journey_colleges')
          .update({ global_college_id: keepCollegeId })
          .eq('global_college_id', mergeCollege.id);
        
        if (updateError) {
          console.error('🔄 mergeGlobalColleges: Error updating journey colleges for', mergeCollege.name, updateError);
          // Continue with other merges even if one fails
        } else {
          console.log('🔄 mergeGlobalColleges: Updated journey colleges for', mergeCollege.name);
        }
      }
      
      // 4. Merge data from duplicate colleges into the keep college (combine best data)
      const mergedData = { ...keepCollege };
      
      mergeColleges.forEach(college => {
        // Use the most complete data available
        if (!mergedData.location && college.location) mergedData.location = college.location;
        if (!mergedData.acceptance_rate && college.acceptance_rate) mergedData.acceptance_rate = college.acceptance_rate;
        if (!mergedData.tuition_in_state && college.tuition_in_state) mergedData.tuition_in_state = college.tuition_in_state;
        if (!mergedData.tuition_out_state && college.tuition_out_state) mergedData.tuition_out_state = college.tuition_out_state;
        if (!mergedData.enrollment_total && college.enrollment_total) mergedData.enrollment_total = college.enrollment_total;
        if (!mergedData.website && college.website) mergedData.website = college.website;
        if (!mergedData.ranking && college.usnwr_ranking) mergedData.ranking = college.usnwr_ranking;
        
        // Combine notes if both exist
        if (college.notes && college.notes !== mergedData.notes) {
          mergedData.notes = mergedData.notes 
            ? `${mergedData.notes}; ${college.notes}` 
            : college.notes;
        }
      });
      
      // 5. Update the keep college with merged data
      const { error: updateKeepError } = await supabase
        .from('global_colleges')
        .update({
          ...mergedData,
          updated_at: new Date().toISOString()
        })
        .eq('id', keepCollegeId);
      
      if (updateKeepError) throw updateKeepError;
      
      // 6. Delete the duplicate colleges
      const { error: deleteError } = await supabase
        .from('global_colleges')
        .delete()
        .in('id', mergeCollegeIds);
      
      if (deleteError) throw deleteError;
      
      console.log('✅ mergeGlobalColleges: Successfully merged', mergeCollegeIds.length, 'colleges into', keepCollege.name);
      
      return {
        success: true,
        keepCollege: mergedData,
        mergedCount: mergeCollegeIds.length,
        message: `Successfully merged ${mergeCollegeIds.length} duplicate colleges into ${keepCollege.name}`
      };
      
    } catch (error) {
      console.error('Error merging global colleges:', error);
      throw error;
    }
  },

  async deleteGlobalCollege(collegeId) {
    try {
      console.log('🗑️ deleteGlobalCollege: Deleting global college', collegeId);
      
      // Check if any journey colleges reference this global college
      const { data: referencingColleges, error: refError } = await supabase
        .from('college_journey_colleges')
        .select('id, name')
        .eq('global_college_id', collegeId);
      
      if (refError) throw refError;
      
      if (referencingColleges && referencingColleges.length > 0) {
        console.log('⚠️ deleteGlobalCollege: Cannot delete - referenced by', referencingColleges.length, 'journey colleges');
        return {
          success: false,
          error: `Cannot delete: ${referencingColleges.length} user colleges reference this global college`,
          referencingColleges
        };
      }
      
      // Safe to delete
      const { error: deleteError } = await supabase
        .from('global_colleges')
        .delete()
        .eq('id', collegeId);
      
      if (deleteError) throw deleteError;
      
      console.log('✅ deleteGlobalCollege: Successfully deleted global college');
      return { success: true };

    } catch (error) {
      console.error('Error deleting global college:', error);
      throw error;
    }
  },

  /**
   * Trigger essay task sync in the background
   * Called after any essay creation/update to keep tasks in sync
   */
  async triggerEssayTaskSync(userId) {
    try {
      // Import dynamically to avoid circular dependencies
      const EssayTaskGenerationService = (await import('../services/EssayTaskGenerationService')).default;

      // Run sync in background (don't await to avoid blocking the update)
      EssayTaskGenerationService.syncEssayTasksWithEpic(supabase, userId)
        .then(result => {
          console.log(`[Essay Sync] Tasks updated for user ${userId}:`, result);
        })
        .catch(error => {
          console.error(`[Essay Sync] Failed to sync tasks for user ${userId}:`, error);
        });
    } catch (error) {
      console.error('[Essay Sync] Error triggering sync:', error);
    }
  }
};

export default collegeJourneyProgressService;